import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Shield, ShieldCheck, Plus, KeyRound, Trash2, Mail, Crown, AlertCircle, Copy, Star } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

/**
 * Admin "Manage Admins" page — admin/faculty add/remove/promote each other,
 * reset passwords back to the canonical default. Refuses self-removal and
 * refuses to remove the seed admin or the last remaining admin.
 */

const DEFAULT_PW = "WeaveAdmin2026!";

export default function AdminManageAdmins() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const iAmSuper = !!user?.is_super_admin;
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Invite dialog
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "admin" });
  const [inviteError, setInviteError] = useState("");
  // Reveal newly-issued credentials
  const [issued, setIssued] = useState(null); // {email, name, password}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admins");
      setAdmins(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load admins");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitInvite = async () => {
    setInviteError("");
    if (!form.email || !form.email.includes("@")) return setInviteError("Provide a valid email");
    if (!form.name.trim()) return setInviteError("Provide a name");
    setBusy(true);
    try {
      const { data } = await api.post("/admins", {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
      });
      setInviting(false);
      setForm({ email: "", name: "", role: "admin" });
      if (data.promoted) {
        toast.success(`${data.user.email} was already a user — promoted to ${data.user.role}`);
      } else {
        setIssued({ email: data.user.email, name: data.user.name, password: data.initial_password || DEFAULT_PW });
        toast.success(`Admin invited — initial password: ${data.initial_password || DEFAULT_PW}`);
      }
      load();
    } catch (e) {
      setInviteError(e?.response?.data?.detail || "Could not invite");
    } finally { setBusy(false); }
  };

  const removeAdmin = async (a) => {
    if (!(await confirm({
      title: `Remove admin access for ${a.email}?`,
      description: "This deletes the user account entirely. Their bookings, submissions, and content references remain in the system.",
      confirmText: "Remove",
      destructive: true,
    }))) return;
    setBusy(true);
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success(`${a.email} removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    } finally { setBusy(false); }
  };

  const resetPassword = async (a) => {
    if (!(await confirm({
      title: `Reset password for ${a.email}?`,
      description: `Their password will be set back to ${DEFAULT_PW}. They should change it on next sign-in.`,
      confirmText: "Reset password",
      destructive: false,
    }))) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admins/${a.id}/reset-password`);
      setIssued({ email: a.email, name: a.name, password: data.initial_password });
      toast.success("Password reset");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset");
    } finally { setBusy(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const downloadLinkedInList = async () => {
    try {
      const res = await api.get("/admin/linkedin-recommendations.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "linkedin-recommendations.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download list");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-manage-admins-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Shield size={12} /> Admin · access control</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Manage admins</h1>
          <p className="text-[#525252] mt-3 max-w-2xl text-sm leading-relaxed">
            Add or remove admin / faculty users. New admins start on the default password{" "}
            <code className="bg-[#0A0A0A] text-white px-2 py-0.5 font-mono">{DEFAULT_PW}</code>{" "}
            — they should change it from <em>Mentor Dashboard → Change my password</em>{" "}
            (admins use the same change-password endpoint).
          </p>
        </div>
        <Button onClick={() => { setInviting(true); setInviteError(""); }} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="invite-admin-btn">
          <Plus size={13} className="mr-2" /> Invite admin
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : admins.length === 0 ? (
        <div className="border border-dashed border-[#E5E7EB] p-8 text-center text-sm text-[#525252]" data-testid="admins-empty">
          No admins yet — this shouldn&apos;t happen!
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admins-list">
          {admins.map((a) => {
            const isSelf = a.id === user?.id;
            const isSeed = a.email === "admin@theweave.co";
            const isOwner = (a.email || "").toLowerCase() === "james@wearetheweave.co.uk";
            const targetIsSuper = !!a.is_super_admin;
            const toggleSuper = async () => {
              const nextState = !targetIsSuper;
              if (!(await confirm({
                title: nextState
                  ? `Make ${a.name} a super admin?`
                  : `Remove super admin from ${a.name}?`,
                description: nextState
                  ? "Super admins can run the destructive Data Reset (wipe cohort data, restore from snapshots, etc.). Only give this to people you fully trust."
                  : "They'll keep their normal admin access — they just won't be able to run Data Reset any more.",
                confirmText: nextState ? "Promote" : "Demote",
                destructive: !nextState,
              }))) return;
              try {
                await api.post(`/admins/${a.id}/toggle-super-admin`, { is_super_admin: nextState });
                toast.success(nextState ? "Super admin granted" : "Super admin removed");
                load();
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not update");
              }
            };
            return (
              <li key={a.id} className="border border-[#E5E7EB] bg-white p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-row-${a.id}`}>
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center font-display font-bold shrink-0">
                    {a.name?.[0] || a.email?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display text-lg font-bold tracking-tight">{a.name}</div>
                      {isSelf && <span className="text-[9px] uppercase tracking-wider bg-[#2596be] text-white px-2 py-0.5 inline-flex items-center gap-1"><Crown size={10} /> You</span>}
                      {isSeed && <span className="text-[9px] uppercase tracking-wider bg-[#f4b847] text-[#0A0A0A] px-2 py-0.5 font-bold">Seed</span>}
                      {targetIsSuper && (
                        <span
                          className="text-[9px] uppercase tracking-wider bg-[#A23232] text-white px-2 py-0.5 inline-flex items-center gap-1 font-bold"
                          title="Can run Data Reset"
                          data-testid={`admin-super-badge-${a.id}`}
                        >
                          <Star size={10} /> Super
                        </span>
                      )}
                      <span className="text-[9px] uppercase tracking-wider border border-[#0A0A0A] px-2 py-0.5">{a.role}</span>
                    </div>
                    <div className="text-xs text-[#525252] mt-1 inline-flex items-center gap-1 truncate">
                      <Mail size={11} /> {a.email}
                    </div>
                    {a.password_updated_at && (
                      <div className="text-[10px] text-[#525252] mt-0.5">
                        Password changed: {new Date(a.password_updated_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {iAmSuper && !isOwner && (
                    <Button
                      onClick={toggleSuper}
                      disabled={busy}
                      variant="outline"
                      className={`rounded-none h-8 text-xs ${targetIsSuper ? "border-[#A23232] text-[#A23232] hover:bg-[#A23232] hover:text-white" : "border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"}`}
                      title={targetIsSuper ? "Remove super admin" : "Grant super admin"}
                      data-testid={`admin-toggle-super-${a.id}`}
                    >
                      <Star size={11} className="mr-1.5" /> {targetIsSuper ? "Remove super" : "Make super admin"}
                    </Button>
                  )}
                  <Button
                    onClick={() => resetPassword(a)}
                    disabled={busy}
                    variant="outline"
                    className="rounded-none border-[#0A0A0A] h-8 text-xs"
                    data-testid={`admin-reset-pw-${a.id}`}
                  >
                    <KeyRound size={11} className="mr-1.5" /> Reset password
                  </Button>
                  <Button
                    onClick={() => removeAdmin(a)}
                    disabled={busy || isSelf || isSeed}
                    variant="outline"
                    className={`rounded-none h-8 text-xs ${isSelf || isSeed ? "border-[#E5E7EB] text-[#525252]" : "border-[#A23232] text-[#A23232] hover:bg-[#A23232] hover:text-white"}`}
                    title={isSelf ? "You can't remove yourself" : isSeed ? "Seed admin can't be removed" : "Remove"}
                    data-testid={`admin-remove-${a.id}`}
                  >
                    <Trash2 size={11} className="mr-1.5" /> Remove
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Invite dialog */}
      <Dialog open={inviting} onOpenChange={(o) => { if (!o) setInviting(false); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="invite-admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Invite a new admin</DialogTitle>
            <DialogDescription className="text-sm text-[#525252] leading-relaxed">
              They&apos;ll get the default password <code className="bg-[#0A0A0A] text-white px-1.5 py-0.5 font-mono">{DEFAULT_PW}</code> and should change it on first sign-in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-eyebrow">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="name@wearetheweave.co.uk" className="rounded-none mt-2" data-testid="invite-email-input" />
            </div>
            <div>
              <Label className="label-eyebrow">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Jane Doe" className="rounded-none mt-2" data-testid="invite-name-input" />
            </div>
            <div>
              <Label className="label-eyebrow">Role</Label>
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="rounded-none mt-2 border border-[#0A0A0A] h-10 w-full px-3 text-sm bg-white"
                data-testid="invite-role-select"
              >
                <option value="admin">admin — full access</option>
                <option value="faculty">faculty — same as admin (legacy label)</option>
              </select>
            </div>
            {inviteError && (
              <div className="border border-[#e93e36] bg-[#FDEDED] px-3 py-2 text-sm text-[#7A1F1F] inline-flex items-center gap-2" data-testid="invite-error">
                <AlertCircle size={14} /> {inviteError}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setInviting(false)} className="rounded-none border-[#0A0A0A]" data-testid="invite-cancel-btn">Cancel</Button>
            <Button onClick={submitInvite} disabled={busy} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="invite-submit-btn">
              <ShieldCheck size={13} className="mr-2" /> {busy ? "Inviting…" : "Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reveal credentials dialog (after invite or reset) */}
      <Dialog open={!!issued} onOpenChange={(o) => { if (!o) setIssued(null); }}>
        <DialogContent className="rounded-none max-w-md" data-testid="issued-credentials-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">Share these credentials</DialogTitle>
            <DialogDescription className="text-sm text-[#525252] leading-relaxed">
              Send this to <strong className="text-[#0A0A0A]">{issued?.name}</strong> via your preferred secure channel. They should sign in and change the password immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-[#E5E7EB] bg-[#FAFAFA] p-3" data-testid="issued-credentials-block">
              <div className="text-[10px] uppercase tracking-wider text-[#525252]">Email</div>
              <div className="flex items-center justify-between gap-2">
                <code className="text-sm font-mono">{issued?.email}</code>
                <button onClick={() => copyToClipboard(issued?.email)} className="text-[#525252] hover:text-[#0A0A0A] p-1" data-testid="copy-email-btn"><Copy size={12} /></button>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-3">Initial password</div>
              <div className="flex items-center justify-between gap-2">
                <code className="text-sm font-mono">{issued?.password}</code>
                <button onClick={() => copyToClipboard(issued?.password)} className="text-[#525252] hover:text-[#0A0A0A] p-1" data-testid="copy-password-btn"><Copy size={12} /></button>
              </div>
            </div>
            <p className="text-[11px] text-[#525252] leading-relaxed">
              They&apos;ll log in via the <strong>Admin</strong> tab on the login page using these credentials.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setIssued(null)} className="rounded-none bg-[#0A0A0A] hover:bg-[#1a1a1a]" data-testid="issued-close-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
